import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import {
  AccumulationChartComponent,
  AccumulationSeriesCollectionDirective,
  AccumulationSeriesDirective,
  AccumulationDataLabel,
  AccumulationLegend,
  AccumulationTooltip,
  Inject,
  PieSeries,
  AccumulationAnnotation,
} from '@syncfusion/ej2-react-charts';
import { Box, Skeleton, Typography } from '@mui/material';
import { MoneyFormatv3 } from '../../../library/MoneyFormat';
import { useThemes } from '../../../components/Theme/theme';

const INITIAL_RADIUS = 90;
const DECREMENT = 3;


// Move utility functions outside component to prevent recreation
const calculateRadius = (index, initialRadius, decrement) => {
  return `${initialRadius - index * decrement}%`;
};

const calculateSum = (dataArray, groupNames) => {
  if (!dataArray?.length) return 0;
  return dataArray
    .filter((item) => groupNames.includes(item.group_name))
    .reduce((sum, item) => sum + (item.balance || 0), 0);
};

const calculateNetProfitLoss = (groupData) => {
  const GROUP_NAMES = {
    TRADING_INCOMES: 'Trading Incomes',
    DIRECT_INCOME: 'Direct Income',
    TRADING_EXPENSE: 'Trading Expense',
    DIRECT_EXPENSE: 'Direct Expense',
  };

  const netIncomeGroups = [GROUP_NAMES.TRADING_INCOMES, GROUP_NAMES.DIRECT_INCOME, 'Indirect Income'];
  const netExpenseGroups = [
    GROUP_NAMES.TRADING_EXPENSE,
    GROUP_NAMES.DIRECT_EXPENSE,
    'Indirect Expenses',
    'Depreciation & Amortisation',
    'Interest Expenses',
    'Tax Expenses'
  ];

  const stockData = groupData?.[0] || {};
  const closingStock = stockData.closingstockamt || 0;
  const openingStock = stockData.openingstockamt || 0;

  const netIncome = calculateSum(groupData, netIncomeGroups) + closingStock;
  const netExpense = calculateSum(groupData, netExpenseGroups) + openingStock;

  const netProfitLoss = netIncome - netExpense;

  return {
    netProfitLoss,
    isNetProfit: netProfitLoss > 0,
    isNetLoss: netProfitLoss < 0,
    netIncome,
    netExpense
  };
};

const processGroupData = (groupData) => {
  if (!groupData || groupData.length === 0) return { innerChartData: [], outerChartData: [] };

  const { netProfitLoss, isNetProfit, isNetLoss, netIncome, netExpense } = calculateNetProfitLoss(groupData);

  const incomeCategories = {
    'Trading Incomes': 'Trading Incomes',
    'Direct Income': 'Direct Income',
    'Indirect Income': 'Indirect Income'
  };

  const expenseCategories = {
    'Trading Expense': 'Trading Expense',
    'Purchases': 'Purchases',
    'Direct Expense': 'Direct Expense',
    'Salary, Benefits & Wage Expenses': 'Salary, Benefits & Wage Expenses',
    'General & Administrative Expenses': 'General & Administrative Expenses',
    'Advertising & Promotion': 'Advertising & Promotion',
    'Professional Services': 'Professional Services',
    'Depreciation & Amortisation': 'Depreciation & Amortisation',
    'Interest Expenses': 'Interest Expenses',
    'Tax Expenses': 'Tax Expenses'
  };

  const incomeData = {};
  let totalIncome = 0;

  Object.keys(incomeCategories).forEach(category => {
    const categoryData = groupData.find(item => item.group_name === category);
    let amount = categoryData ? Math.abs(categoryData.balance) : 0;

    if (category === 'Indirect Income' && categoryData) {
      if (amount === 0) {
        amount = 0.01;
      } else if (amount < 1) {
        amount = Math.max(amount, 0.1);
      }
    }

    if (category === 'Direct Income' && categoryData && amount < 1) {
      amount = Math.max(amount, 0.1);
    }

    incomeData[category] = amount;
    totalIncome += amount;
  });

  if (isNetLoss && Math.abs(netProfitLoss) > 0) {
    incomeData['Net Loss'] = Math.abs(netProfitLoss);
    totalIncome += Math.abs(netProfitLoss);
  }

    const innerChartData = Object.keys(incomeData).map((category, index) => {
      const amount = incomeData[category];
      const percentage = totalIncome > 0 ? (amount / totalIncome) * 100 : 0;

      return {
        category: category,
        amount: amount,
        percentage: percentage
      };
    }).filter(item => {
      return item.amount > 0 && item.percentage > 0;
    });

    // Apply rounding logic for Net Loss to ensure total is exactly 100%
    const netLossItem = innerChartData.find(item => item.category === 'Net Loss');
    if (netLossItem) {
      const otherItemsTotal = innerChartData
        .filter(item => item.category !== 'Net Loss')
        .reduce((sum, item) => sum + item.percentage, 0);
      
      const remainingPercentage = 100 - otherItemsTotal;
      if (remainingPercentage > 0) {
        netLossItem.percentage = remainingPercentage;
      }
    }

  const expenseData = {};
  let totalExpenses = 0;

  Object.keys(expenseCategories).forEach(category => {
    const categoryData = groupData.find(item => item.group_name === category);
    let amount = 0;

    if (category === 'Trading Expense') {
      const tradingExpenseData = categoryData;
      const purchaseData = groupData.find(item => item.group_name === 'Purchases');

      if (tradingExpenseData) {
        const openingStock = tradingExpenseData.openingstockamt || 0;
        const purchase = purchaseData ? Math.abs(purchaseData.balance) : 0;
        const closingStock = tradingExpenseData.closingstockamt || 0;

        amount = openingStock + purchase - closingStock;

        if (amount <= 0) {
          amount = Math.max(Math.abs(amount), 1000);
        }
      } else {
        amount = categoryData ? Math.abs(categoryData.balance) : 1000;
      }
    } else {
      amount = categoryData ? Math.abs(categoryData.balance) : 0;
    }

    expenseData[category] = amount;
    totalExpenses += amount;
  });

  const indirectExpenseSubGroups = groupData.filter(item =>
    item.group_path && item.group_path.startsWith('/25/') && item.group_name !== 'Indirect Expenses' && item.group_name !== 'Indirect Income'
  );

  indirectExpenseSubGroups.forEach(item => {
    const amount = Math.abs(item.balance);
    expenseData[item.group_name] = amount;
    totalExpenses += amount;
  });

  if (isNetProfit && netProfitLoss > 0) {
    expenseData['Net Profit'] = netProfitLoss;
    totalExpenses += netProfitLoss;
  }

  const expenseEntries = Object.keys(expenseData)
    .map(category => {
      let amount = expenseData[category];

      if ((category === 'Trading Expense' || category === 'General & Administrative Expenses') && amount === 0) {
        amount = 0.01;
      } else if (category === 'Trading Expense' && amount < 500) {
        amount = Math.max(amount, 500);
      } else if (category === 'General & Administrative Expenses' && amount < 10) {
        amount = Math.max(amount, 10);
      }

      return {
        label: category,
        amount: amount,
        percentage: totalExpenses > 0 ? (amount / totalExpenses) * 100 : 0
      };
    })
    .filter(item => {
      const hasValidAmount = item.amount > 0 || item.label === 'Trading Expense' || item.label === 'General & Administrative Expenses';
      const hasValidPercentage = item.percentage > 0;
      const isNotPurchases = item.label !== 'Purchases';
      return hasValidAmount && hasValidPercentage && isNotPurchases;
    })
    .sort((a, b) => b.amount - a.amount);

  const largeSlices = expenseEntries.filter(item => item.percentage > 8);
  const smallSlices = expenseEntries.filter(item => item.percentage <= 8);

  // Group items: first 7 by percentage, rest as "Others" (excluding Net Profit)
  const sortedExpenseEntries = [...expenseEntries].sort((a, b) => b.percentage - a.percentage);
  const top7Items = sortedExpenseEntries.slice(0, 7);
  const remainingItems = sortedExpenseEntries.slice(7);
  
  // Calculate "Others" total (excluding Net Profit)
  const othersItems = remainingItems.filter(item => item.label !== 'Net Profit');
  const netProfitItem = remainingItems.find(item => item.label === 'Net Profit');
  
  let processedExpenseEntries = [...top7Items];
  
  // Add "Others" group if there are items to group
  if (othersItems.length > 0) {
    const othersTotal = othersItems.reduce((sum, item) => sum + item.amount, 0);
    const othersPercentage = othersItems.reduce((sum, item) => sum + item.percentage, 0);
    
    processedExpenseEntries.push({
      label: 'Others',
      amount: othersTotal,
      percentage: othersPercentage,
      isOthers: true
    });
  }
  
  // Add Net Profit separately if it exists
  if (netProfitItem) {
    processedExpenseEntries.push(netProfitItem);
  }

  // Apply rounding logic for Net Profit to ensure total is exactly 100%
  const netProfitItemForRounding = processedExpenseEntries.find(item => item.label === 'Net Profit');
  if (netProfitItemForRounding) {
    const otherItemsTotal = processedExpenseEntries
      .filter(item => item.label !== 'Net Profit')
      .reduce((sum, item) => sum + item.percentage, 0);
    
    const currentTotal = otherItemsTotal + netProfitItemForRounding.percentage;
    const difference = 100 - currentTotal;
    
    console.log('🔍 Net Profit Rounding Debug:', {
      otherItemsTotal: otherItemsTotal.toFixed(4),
      netProfitOriginal: netProfitItemForRounding.percentage.toFixed(4),
      currentTotal: currentTotal.toFixed(4),
      difference: difference.toFixed(4),
      newNetProfit: (netProfitItemForRounding.percentage + difference).toFixed(4)
    });
    
    // Adjust Net Profit percentage to make total exactly 100%
    netProfitItemForRounding.percentage = netProfitItemForRounding.percentage + difference;
    
    // Ensure Net Profit percentage is not negative
    if (netProfitItemForRounding.percentage < 0) {
      netProfitItemForRounding.percentage = 0.01; // Minimum value
    }
  }

  const outerChartData = processedExpenseEntries.map((item, index) => ({
    ...item,
    radius: calculateRadius(index, INITIAL_RADIUS, DECREMENT),
    isLargeSlice: item.percentage > 8
  }));

  return { innerChartData, outerChartData };
};

const datalabel = {
  visible: true,
  position: 'Inside',
  maxWidth: 3,
  textWrap: 'Wrap',
  connectorStyle: { length: '20px', width: 1 },
  name: 'text',
};

const datalabelDummy = {
  visible: true,
  position: 'Inside',
  maxWidth: 105,
  textWrap: 'Wrap',
  connectorStyle: { length: '1px', width: 1 },
  name: 'text',
};




const PieChart = ({ year, id, groupData, loading = true, title }) => {
  const { theme } = useThemes();

  // Memoize theme-dependent values to prevent infinite re-renders
  const themeValues = useMemo(() => ({
    isDarkTheme: theme?.palette?.TmId?.id?.toString().includes('9'),
    textColor: theme?.palette?.TmId?.id?.toString().includes('9') ? '#ffff' : '#080000',
    noDataColor: theme?.palette?.TmId?.id?.toString().includes('9') ? 'rgba(255,255,255,0.6)' : 'rgba(8,0,0,0.6)'
  }), [theme?.palette?.TmId?.id]);

  // Render count tracker to monitor infinite loops
  const renderCountRef = useRef(0);
  const prevPropsRef = useRef({ year, id, groupData, loading, title });

  useEffect(() => {
    renderCountRef.current += 1;
    console.log(`🔄 PieChart Render #${renderCountRef.current}`, {
      id,
      year,
      loading,
      title,
      groupDataLength: groupData?.length || 0,
      prevProps: prevPropsRef.current
    });

    // Check if props changed
    const currentProps = { year, id, groupData, loading, title };
    const propsChanged = JSON.stringify(currentProps) !== JSON.stringify(prevPropsRef.current);

    if (propsChanged) {
      console.log('📊 Props changed:', {
        from: prevPropsRef.current,
        to: currentProps
      });
    } else {
      console.log('⚠️ Props unchanged - potential infinite loop!');
    }

    prevPropsRef.current = currentProps;

    // Alert if too many renders
    if (renderCountRef.current > 10) {
      console.error(`🚨 INFINITE LOOP DETECTED! PieChart has rendered ${renderCountRef.current} times`);
    }
  });


  const processGroupData = (groupData) => {
    if (!groupData || groupData.length === 0) return { innerChartData: [], outerChartData: [] };

    const { netProfitLoss, isNetProfit, isNetLoss, netIncome, netExpense } = calculateNetProfitLoss(groupData);

    const incomeCategories = {
      'Trading Incomes': 'Trading Incomes',
      'Direct Income': 'Direct Income',
      'Indirect Income': 'Indirect Income'
    };

    const expenseCategories = {
      'Trading Expense': 'Trading Expense',
      'Purchases': 'Purchases',
      'Direct Expense': 'Direct Expense',
      'Salary, Benefits & Wage Expenses': 'Salary, Benefits & Wage Expenses',
      'General & Administrative Expenses': 'General & Administrative Expenses',
      'Advertising & Promotion': 'Advertising & Promotion',
      'Professional Services': 'Professional Services',
      'Depreciation & Amortisation': 'Depreciation & Amortisation',
      'Interest Expenses': 'Interest Expenses',
      'Tax Expenses': 'Tax Expenses'
    };

    const incomeData = {};
    let totalIncome = 0;

    Object.keys(incomeCategories).forEach(category => {
      const categoryData = groupData.find(item => item.group_name === category);
      let amount = categoryData ? Math.abs(categoryData.balance) : 0;

      if (category === 'Indirect Income' && categoryData) {
        if (amount === 0) {
          amount = 0.01;
        } else if (amount < 1) {
          amount = Math.max(amount, 0.1);
        }
      }

      if (category === 'Direct Income' && categoryData && amount < 1) {
        amount = Math.max(amount, 0.1);
      }

      incomeData[category] = amount;
      totalIncome += amount;
    });

    if (isNetLoss && Math.abs(netProfitLoss) > 0) {
      incomeData['Net Loss'] = Math.abs(netProfitLoss);
      totalIncome += Math.abs(netProfitLoss);
    }

    const innerChartData = Object.keys(incomeData).map((category, index) => {
      const amount = incomeData[category];
      const percentage = totalIncome > 0 ? (amount / totalIncome) * 100 : 0;

      return {
        category: category,
        amount: amount,
        percentage: percentage
      };
    }).filter(item => {
      return item.amount > 0 && item.percentage > 0;
    });

    // Apply rounding logic for Net Loss to ensure total is exactly 100%
    const netLossItem = innerChartData.find(item => item.category === 'Net Loss');
    if (netLossItem) {
      const otherItemsTotal = innerChartData
        .filter(item => item.category !== 'Net Loss')
        .reduce((sum, item) => sum + item.percentage, 0);
      
      const remainingPercentage = 100 - otherItemsTotal;
      if (remainingPercentage > 0) {
        netLossItem.percentage = remainingPercentage;
      }
    }

    const expenseData = {};
    let totalExpenses = 0;

    Object.keys(expenseCategories).forEach(category => {
      const categoryData = groupData.find(item => item.group_name === category);
      let amount = 0;

      if (category === 'Trading Expense') {
        const tradingExpenseData = categoryData;
        const purchaseData = groupData.find(item => item.group_name === 'Purchases');

        if (tradingExpenseData) {
          const openingStock = tradingExpenseData.openingstockamt || 0;
          const purchase = purchaseData ? Math.abs(purchaseData.balance) : 0;
          const closingStock = tradingExpenseData.closingstockamt || 0;

          amount = openingStock + purchase - closingStock;

          if (amount <= 0) {
            amount = Math.max(Math.abs(amount), 1000);
          }
        } else {
          amount = categoryData ? Math.abs(categoryData.balance) : 1000;
        }
      } else {
        amount = categoryData ? Math.abs(categoryData.balance) : 0;
      }

      expenseData[category] = amount;
      totalExpenses += amount;
    });

    const indirectExpenseSubGroups = groupData.filter(item =>
      item.group_path && item.group_path.startsWith('/25/') && item.group_name !== 'Indirect Expenses' && item.group_name !== 'Indirect Income'
    );

    indirectExpenseSubGroups.forEach(item => {
      const amount = Math.abs(item.balance);
      expenseData[item.group_name] = amount;
      totalExpenses += amount;
    });

    if (isNetProfit && netProfitLoss > 0) {
      expenseData['Net Profit'] = netProfitLoss;
      totalExpenses += netProfitLoss;
    }

    const expenseEntries = Object.keys(expenseData)
      .map(category => {
        let amount = expenseData[category];

        if ((category === 'Trading Expense' || category === 'General & Administrative Expenses') && amount === 0) {
          amount = 0.01;
        } else if (category === 'Trading Expense' && amount < 500) {
          amount = Math.max(amount, 500);
        } else if (category === 'General & Administrative Expenses' && amount < 10) {
          amount = Math.max(amount, 10);
        }
        return {
          label: category,
          amount: amount,
          percentage: totalExpenses > 0 ? (amount / totalExpenses) * 100 : 0
        };
      })
      .filter(item => {
        const hasValidAmount = item.amount > 0 || item.label === 'Trading Expense' || item.label === 'General & Administrative Expenses';
        const hasValidPercentage = item.percentage > 0;
        const isNotPurchases = item.label !== 'Purchases';
        return hasValidAmount && hasValidPercentage && isNotPurchases;
      })
      .sort((a, b) => b.amount - a.amount);

    const largeSlices = expenseEntries.filter(item => item.percentage > 8);
    const smallSlices = expenseEntries.filter(item => item.percentage <= 8);

    // Group items: first 7 by percentage, rest as "Others" (excluding Net Profit)
    const sortedExpenseEntries = [...expenseEntries].sort((a, b) => b.percentage - a.percentage);
    const top7Items = sortedExpenseEntries.slice(0, 7);
    const remainingItems = sortedExpenseEntries.slice(7);
    
    // Calculate "Others" total (excluding Net Profit)
    const othersItems = remainingItems.filter(item => item.label !== 'Net Profit');
    const netProfitItem = remainingItems.find(item => item.label === 'Net Profit');
    
    let processedExpenseEntries = [...top7Items];
    
    // Add "Others" group if there are items to group
    if (othersItems.length > 0) {
      const othersTotal = othersItems.reduce((sum, item) => sum + item.amount, 0);
      const othersPercentage = othersItems.reduce((sum, item) => sum + item.percentage, 0);
      
      processedExpenseEntries.push({
        label: 'Others',
        amount: othersTotal,
        percentage: othersPercentage,
        isOthers: true
      });
    }
    
    // Add Net Profit separately if it exists
    if (netProfitItem) {
      processedExpenseEntries.push(netProfitItem);
    }

    // Apply rounding logic for Net Profit to ensure total is exactly 100%
    const netProfitItemForRounding = processedExpenseEntries.find(item => item.label === 'Net Profit');
    if (netProfitItemForRounding) {
      const otherItemsTotal = processedExpenseEntries
        .filter(item => item.label !== 'Net Profit')
        .reduce((sum, item) => sum + item.percentage, 0);
      
      const currentTotal = otherItemsTotal + netProfitItemForRounding.percentage;
      const difference = 100 - currentTotal;
      
      console.log('🔍 Net Profit Rounding Debug (Instance 2):', {
        otherItemsTotal: otherItemsTotal.toFixed(4),
        netProfitOriginal: netProfitItemForRounding.percentage.toFixed(4),
        currentTotal: currentTotal.toFixed(4),
        difference: difference.toFixed(4),
        newNetProfit: (netProfitItemForRounding.percentage + difference).toFixed(4)
      });
      
      // Adjust Net Profit percentage to make total exactly 100%
      netProfitItemForRounding.percentage = netProfitItemForRounding.percentage + difference;
      
      // Ensure Net Profit percentage is not negative
      if (netProfitItemForRounding.percentage < 0) {
        netProfitItemForRounding.percentage = 0.01; // Minimum value
      }
    }

    const outerChartData = processedExpenseEntries.map((item, index) => ({
      ...item,
      radius: calculateRadius(index, INITIAL_RADIUS, DECREMENT),
      isLargeSlice: item.percentage > 8
    }));

    return { innerChartData, outerChartData };
  };



  // Memoize expensive data processing to prevent infinite re-renders
  const { innerChartData: rawInnerChartData, outerChartData } = useMemo(() => {
    return processGroupData(groupData);
  }, [groupData]);



  // Memoize palette arrays to prevent recreation
  const paletteArrays = useMemo(() => {
    const paletteSetOne = ['#ff7b00', '#ff8800', '#ff9500', '#FB8C00', '#FC9E1A', '#FDB033', '#FEC24C'];
    const paletteSetTwo = ['#911B7C', '#9D2B84', '#A93B8C', '#B54B94', '#C15B9C', '#CD6BA4', '#D97BAC', '#E58BB4'];
    const dummyPaletteSetTwo = ['#C88DBE', '#CE96C2', '#D49FC6', '#DAA8CA', '#E0B1CE', '#E6BAD2', '#EBBDD6', '#F1C6DA'];
    const dummyPaletteSetOne = ['#E8B980', '#E8BA82', '#E8BC85', '#FCC480', '#FDD09D', '#FED9B3', '#FEE5C0'];

    return {
      outerPalettes: id === 'chartdiv1' ? [...paletteSetOne].reverse() : [...paletteSetTwo].reverse(),
      innerPalettes: id === 'chartdiv1' ? [...paletteSetTwo].reverse() : [...paletteSetOne].reverse(),
      dummyPalettes: id === 'chartdiv1' ? [...dummyPaletteSetOne].reverse() : [...dummyPaletteSetTwo].reverse()
    };
  }, [id]);

  // Memoize inner chart data processing
  const innerChartData = useMemo(() => {
    return rawInnerChartData.map((item, index) => {
      const colorShades = {
        'chartdiv1': [
          '#b5e48c',
          '#52b69a',
          '#b5e48c',
          '#52b69a',
          '#b5e48c'
        ],
        'chartdiv2': [
          '#f69ba6',
          '#f27e9e',
          '#f69ba6',
          '#f27e9e',
          '#f69ba6'
        ]
      };

      const alphabetLabel = String.fromCharCode(97 + index);
      const percentageLabel = `${item.percentage.toFixed(2)}%`;
      const chartColors = colorShades[id] || colorShades['chartdiv1'];
      
      // Use specific colors for very small slices (less than 5%)
      let color = chartColors[index % chartColors.length];
      if (item.percentage < 5) {
        color = id === 'chartdiv2' ? '#7400b8' : '#023e8a';
      }

      return {
        ...item,
        color: color,
        alphabetLabel: percentageLabel,
        legendLabel: alphabetLabel
      };
    });
  }, [rawInnerChartData, id]);

  // Memoize event handlers to prevent recreation
  const textRender = useCallback((args) => {
    args.font.fontWeight = 'bold';
    args.font.fontStyle = 'roboto';
    const percentage = args.point.percentage ? args.point.percentage.toFixed(2) : '0.00';

    let label = args.point.x;

    const isInnerChart = args.series.name === 'Income';

    // Hide labels for 0% items
    if (parseFloat(percentage) === 0) {
      args.text = '';
      return;
    }

    if (isInnerChart) {
      args.text = args.point.x || '';
      args.font.size = '14px';
      args.font.fontWeight = 'bold';
      args.font.color = 'white';
    } else {
      const screenWidth = window.innerWidth;
      let fontSize = '11px';
      if (screenWidth < 600) {
        fontSize = '9px';
      } else if (screenWidth < 900) {
        fontSize = '10px';
      }

      args.font.size = fontSize;
      args.font.color = '#000000';
      args.font.fontWeight = 'bold';

      args.text = `${label}<br>${percentage}%`;
      args.point.labelPosition = 'Outside';
      if (args.series && args.series.dataLabel && args.series.dataLabel.connectorStyle) {
        args.series.dataLabel.connectorStyle.length = '0px';
      }
    }
  }, []);







  const tooltipRender = useCallback((args) => {
    const isInnerChart = args.series.name === 'Income';
    const chartType = isInnerChart ? 'Income' : 'Expense';

    args.headerText = `${chartType} Details`;
    args.text = `${args.point.x}: ${MoneyFormatv3(args.point.y)}`;

    const tooltipElement = document.querySelector('.e-tooltip-wrap.e-popup');
    if (tooltipElement) {
      const chartElement = document.getElementById(`${args.series.chart.element.id}`);
      const sliceElement = args.point.symbolLocations[0];

      if (chartElement && sliceElement) {
        const chartRect = chartElement.getBoundingClientRect();
        const offsetX = sliceElement.x + chartRect.left + window.scrollX;
        const offsetY = sliceElement.y + chartRect.top + window.scrollY;

        const tooltipWidth = tooltipElement.offsetWidth || 0;
        const tooltipHeight = tooltipElement.offsetHeight || 0;

        tooltipElement.style.left = `${offsetX - tooltipWidth / 2}px`;
        tooltipElement.style.top = `${offsetY - tooltipHeight - 10}px`;
        tooltipElement.style.transform = 'none';
      }
    }
  }, []);


  const dummytextRender = (args) => {
    args.font.fontWeight = 'bold';
    args.font.fontStyle = 'roboto';
    const screenWidth = window.innerWidth;
    let fontSize = '11px';
    if (screenWidth < 600) {
      fontSize = '9px';
    } else if (screenWidth < 900) {
      fontSize = '10px';
    }
    args.font.opacity = 0.3;
    args.text = `${args.point.x}`;
  };
  


  // Memoize animation objects to prevent recreation
  const animationConfig = useMemo(() => ({
    enable: true,
    duration: 2000,
    delay: 500,
    easing: 'EaseOut',
  }), []);

  // Memoize margin objects to prevent recreation
  const marginConfig = useMemo(() => ({
    top: 20,
    bottom: 20,
    left: 20,
    right: 20
  }), []);

  const marginConfigLarge = useMemo(() => ({
    top: 60,
    bottom: 60,
    left: 80,
    right: 80
  }), []);

  return (
    <Box sx={{ borderRadius: 1, backgroundColor: 'transparent', }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: id === 'chartdiv2' ? 'flex-end' : 'flex-start', height: 3, py: 2 }}>
        <Typography
          sx={{
            pl: 1,
            pl: 1,
            color: themeValues.textColor,
            fontWeight: 'bold',
            fontSize: "16px",
          }}

        >
          {title || 'FY: Not selected'}
        </Typography>
      </Box>

      <Box
        sx={{
          height: { xs: 600, sm: 500, md: 650 },
          mt: { sm: 1, xs: 0 },
          position: 'relative',
          width: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'center', md: 'stretch' },
        }}
      >
        {loading ? (
          <Box
            sx={{
              position: 'absolute',
              top: '40%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '18px',
              color: themeValues.textColor,
            }}
          >
            <Skeleton variant="circular" width={300} height={300} />
          </Box>
        ) : (
          <>
            {innerChartData?.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '40%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '100%' ,
                  height:  '100%' ,
                  zIndex: 1,
                  pointerEvents: 'auto',
                }}
              >
                <AccumulationChartComponent
                  id={`${id}-inner`}
                  legendSettings={{ visible: false }}
                  enableSmartLabels={true}
                  enableAnimation={true}
                  style={{ pointerEvents: 'auto' }}
                  tooltip={{
                    enable: true,
                    format: '${point.x}: ${point.y}',
                    header: 'Income Details'
                  }}
                  palettes={paletteArrays.innerPalettes}
                  textRender={textRender}
                  margin={marginConfig}
                  animation={animationConfig}
                >
                  <Inject services={[AccumulationLegend, PieSeries, AccumulationDataLabel, AccumulationTooltip]} />
                  <AccumulationSeriesCollectionDirective>
                    <AccumulationSeriesDirective
                      dataSource={innerChartData}
                      name="Income"
                      xName="alphabetLabel"
                      yName="amount"
                      innerRadius="0%"
                      radius="23%"
                      pointColorMapping="color"
                      dataLabel={{
                        visible: true,
                        font: {
                          size: '14px',
                          fontWeight: 'bold',
                          color: 'white'
                        },
                        position: 'Inside',
                        connectorStyle: { length: '10px', width: 1 },
                        maxWidth: 50,
                        textWrap: 'Wrap',
                      }}
                      animation={animationConfig}
                    />
                  </AccumulationSeriesCollectionDirective>
                </AccumulationChartComponent>
              </Box>
            )}


            {outerChartData?.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '40%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  margin: 'auto',
                  width:  '100%' ,
                  height:  '100%' ,
                  borderRadius: '8px',
                  zIndex: 2,
                  pointerEvents: 'auto',
                }}
              >
                <AccumulationChartComponent
                  id={`${id}-outer`}
                  legendSettings={{ visible: false }}
                  enableSmartLabels={true}
                  enableAnimation={true}
                  style={{ pointerEvents: 'auto' }}
                  style={{ pointerEvents: 'auto' }}
                  tooltip={{
                    enable: true,
                    format: '${point.x}: ${point.y}',
                    header: 'Expense Details',
                  }}
                  textRender={textRender}
                  enableRotation={true}
                  depth={30}
                  angle={45}
                  margin={marginConfigLarge}
                  animation={animationConfig}
                >
                  <Inject
                    services={[
                      AccumulationLegend,
                      PieSeries,
                      AccumulationDataLabel,
                      AccumulationTooltip,
                      AccumulationAnnotation,
                    ]}
                  />

                  <AccumulationSeriesCollectionDirective>
                    <AccumulationSeriesDirective
                      dataSource={outerChartData}
                      palettes={paletteArrays.outerPalettes}
                      name="Expenses"
                      xName="label"
                      yName="amount"
                      innerRadius="30%"
                      radius="radius"
                      pointColorMapping="color"
                      dataLabel={datalabel}
                      animation={animationConfig}
                    />
                  </AccumulationSeriesCollectionDirective>
                </AccumulationChartComponent>


              </Box>
            )}

            {innerChartData?.length > 0 && outerChartData?.length === 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '40%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  margin: 'auto',
                  width: { xs: '85%', sm: '90%', md: '100%' },
                  height: { xs: '85%', sm: '90%', md: '100%' },
                  borderRadius: '8px',
                  zIndex: 2,
                  pointerEvents: 'auto',
                }}
              >
                <AccumulationChartComponent
                  id={`${id}-inner-only`}
                  id={`${id}-inner-only`}
                  legendSettings={{ visible: false }}
                  enableSmartLabels={true}
                  enableAnimation={true}
                  style={{ pointerEvents: 'auto' }}
                  style={{ pointerEvents: 'auto' }}
                  tooltip={{
                    enable: true,
                    format: '${point.x}: ${point.y}',
                    header: 'Income Details'
                  }}
                  palettes={paletteArrays.innerPalettes}
                  textRender={textRender}
                  margin={marginConfig}
                  animation={animationConfig}
                >
                  <Inject services={[AccumulationLegend, PieSeries, AccumulationDataLabel, AccumulationTooltip]} />
                  <AccumulationSeriesCollectionDirective>
                    <AccumulationSeriesDirective
                      dataSource={innerChartData}
                      name="Income"
                      xName="alphabetLabel"
                      yName="amount"
                      innerRadius="0%"
                      radius="70%"
                      pointColorMapping="color"
                      dataLabel={{
                        visible: true,
                        font: {
                          size: '14px',
                          fontWeight: 'bold',
                          color: 'white'
                        },
                        position: 'Inside',
                        connectorStyle: { length: '10px', width: 1 },
                        maxWidth: 50,
                        textWrap: 'Wrap',
                      }}
                      animation={animationConfig}
                    />
                  </AccumulationSeriesCollectionDirective>
                </AccumulationChartComponent>
              </Box>
            )}

            {innerChartData?.length === 0 && outerChartData?.length === 0 && (
              <>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '40%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    margin: 'auto',
                    width: '100%',
                    height: '100%',
                    borderRadius: '8px',
                  }}
                >
                  <AccumulationChartComponent
                    id={`${id}-dummy`}
                    legendSettings={{ visible: false }}
                    enableSmartLabels={true}
                    enableAnimation={true}
                    tooltip={{
                      enable: false,
                      showNearestPoint: false,
                    }}
                    textRender={dummytextRender}
                    depth={30}
                    angle={45}
                    animation={{
                      enable: true,
                      duration: 2000,
                      delay: 500,
                      easing: 'EaseOut',
                    }}
                  >
                    <Inject
                      services={[AccumulationLegend, PieSeries, AccumulationDataLabel, AccumulationTooltip, AccumulationAnnotation]}
                    />
                    <AccumulationSeriesCollectionDirective>
                      <AccumulationSeriesDirective
                        palettes={paletteArrays.dummyPalettes}
                        dataSource={[
                          {
                            category: 'Trading expenses',
                            amount:150,
                            radius: calculateRadius(0, 80, DECREMENT),
                          },
                          {
                            category: 'Direct Expenses',
                            amount: 85,
                            radius: calculateRadius(1, 80, DECREMENT),
                          },
                          {
                            category: 'Salary, Benefits & Wage Expenses',
                            amount: 80,
                            radius: calculateRadius(2, 80, DECREMENT),
                          },
                          {
                            category: 'General & Administrative Expenses',
                            amount: 75,
                            radius: calculateRadius(3, 80, DECREMENT),
                          },
                          {
                            category: 'Depreciation & Amortisation',
                            amount: 60,
                            radius: calculateRadius(4, 80, DECREMENT),
                          },
                          {
                            category: 'Interest Expenses',
                            amount: 45,
                            radius: calculateRadius(5, 80, DECREMENT),
                          },
                          {
                            category: 'Tax Expenses',
                            amount: 30,
                            radius: calculateRadius(6, 80, DECREMENT),
                          },
                        ]}
                        name="No Data"
                        xName="category"
                        yName="amount"
                        explode={false}
                        innerRadius="30%"
                        radius="radius"
                        pointColorMapping="color"
                        dataLabel={datalabelDummy}
                        animation={animationConfig}
                      />

                      
                    </AccumulationSeriesCollectionDirective>
                  </AccumulationChartComponent>
                </Box>

                <Box
                  sx={{
                    position: 'absolute',
                    top: '40%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '18px',
                    color: themeValues.noDataColor,
                  }}
                >
                  No Data
                </Box>
              </>
            )}
          </>
        )}

        {innerChartData?.length > 0 && (
          <Box
            sx={{
              position: { xs: 'static', sm: 'static', md: 'static', lg: 'absolute' },
              right: { lg: '10px' },
              top: { lg: '50%' },
              transform: { lg: 'translateY(-50%)' },
              width: { xs: '100%', sm: '100%', md: '100%', lg: '25%' },
              maxHeight: { xs: '180px', sm: '200px', md: '200px', lg: '400px' },
              overflowY: 'auto',
              padding: { xs: '12px 0', sm: '12px 0', md: '12px 0', lg: '10px 0 0 0' },
              backgroundColor: 'transparent',
              borderRadius: 0,
              backdropFilter: 'none',
              boxShadow: 'none',
              border: 'none',
              display: 'flex',
              alignItems: { xs: 'center', sm: 'center', md: 'center', lg: 'center' },
              justifyContent: { xs: 'center', sm: 'center', md: 'center', lg: 'flex-end' },
              zIndex: 10,
              mt: { xs: 2, sm: 2, md: 2, lg: 0 },
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'row', sm: 'row', md: 'row', lg: 'column' },
              flexWrap: { xs: 'wrap', sm: 'wrap', md: 'wrap', lg: 'nowrap' },
              gap: { xs: '8px', sm: '8px', md: '8px', lg: '6px' },
              justifyContent: { xs: 'center', sm: 'center', md: 'center', lg: 'flex-start' },
              alignItems: { xs: 'center', sm: 'center', md: 'center', lg: 'stretch' },
              width: { xs: '100%', sm: '100%', md: '100%', lg: 'auto' },
            }}>
              {innerChartData.map((item, index) => {
                return (
                  <Box 
                    key={index} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      padding: { xs: '4px 8px', md: 0 },
                      backgroundColor: { xs: 'rgba(0,0,0,0.05)', md: 'transparent' },
                      border: { xs: '1px solid rgba(0,0,0,0.1)', md: 'none' },
                      borderRadius: { xs: '4px', md: 0 },
                      minWidth: { xs: 'auto', md: 'auto' },
                      flex: { xs: '0 0 auto', md: 'none' },
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: '8px', sm: '8px', md: '6px' },
                        height: { xs: '8px', sm: '8px', md: '6px' },
                        borderRadius: '50%',
                        backgroundColor: item.color,
                        marginRight: { xs: '6px', sm: '6px', md: '4px' },
                        flexShrink: 0,
                        boxShadow: 'none',
                        border: 'none',
                      }}
                    />
                    <Box sx={{ 
                      flex: 1, 
                      minWidth: 0, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: { xs: 'center', md: 'flex-start' },
                      gap: '0px'
                    }}>
                    <Typography 
                      sx={{ 
                        fontSize: { xs: '12px', sm: '13px', md: '14px' }, 
                        color: '#1a1a1a', 
                        fontWeight: '600',
                        lineHeight: 1.3,
                        flex: 1,
                        wordBreak: 'break-word',
                        hyphens: 'auto',
                        margin: 0,
                        textAlign: { xs: 'center', md: 'left' },
                      }}
                    >
                      {item.category}: {''}
                      <span style={{
                        fontSize: { xs: '11px', sm: '12px', md: '12px' },
                        color: '#1a1a1a',
                        fontWeight: '600',
                        backgroundColor: 'transparent',
                        padding: 0,
                        borderRadius: 0,
                        border: 'none',
                        marginLeft: '2px'
                      }}>
                        {item.percentage.toFixed(2)}%
                      </span>
                    </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PieChart;
